import { useEffect, useState } from "react";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "inphase_cookie_consent";

/**
 * Fixed bottom cookie-consent banner. Mobile-first: single column that
 * stacks the text above the buttons, buttons wrap into two rows if the
 * viewport is narrower than ~360px, text never degrades into
 * one-word-per-line (max-width is scoped to the available container,
 * not a parent that collapsed around a long button).
 *
 * Reads/writes a single localStorage key; renders nothing after a
 * decision is recorded. "Learn more" expands an inline details block
 * instead of taking the user off-page.
 */
export default function CookieBanner() {
    const [visible, setVisible] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        try {
            if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
        } catch {
            // localStorage blocked (private mode / iframe sandbox) —
            // just show the banner; user action still works per-session.
            setVisible(true);
        }
    }, []);

    const record = (choice) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, at: new Date().toISOString() }));
        } catch { /* ignore */ }
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            role="dialog"
            aria-label="Cookie consent"
            data-testid="cookie-banner"
            className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 sm:px-4 sm:pb-4 pointer-events-none"
        >
            <div className="pointer-events-auto mx-auto max-w-3xl bg-card/95 backdrop-blur border border-border shadow-xl">
                <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                    <div className="hidden sm:flex shrink-0 w-9 h-9 rounded-full bg-primary/15 text-primary items-center justify-center">
                        <Cookie className="w-4 h-4" />
                    </div>

                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 sm:hidden mb-2">
                            <Cookie className="w-4 h-4 text-primary" />
                            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Cookies</span>
                        </div>
                        <p className="text-sm text-foreground leading-relaxed">
                            We use cookies to keep you signed in and remember your preferences — nothing is sold or shared. By clicking <span className="font-medium">Accept</span>, you agree to our use of cookies.
                        </p>

                        {expanded && (
                            <div className="mt-3 border-t border-border/60 pt-3 space-y-2 text-xs text-muted-foreground font-mono" data-testid="cookie-banner-details">
                                <div><span className="text-foreground">Essential · always on</span> — your login session, CSRF protection.</div>
                                <div><span className="text-foreground">Functional</span> — your UI preferences (table columns, saved filters, theme).</div>
                                <div><span className="text-foreground">Analytics</span> — anonymous page views only; no third-party ad trackers.</div>
                                <div>Declining disables only the Functional + Analytics groups; Essential cookies are required to use the app.</div>
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            data-testid="cookie-banner-learn-more"
                            className="mt-2 text-xs font-mono text-primary hover:underline"
                        >
                            {expanded ? "Hide details" : "Learn more"}
                        </button>
                    </div>

                    <div className="flex flex-wrap gap-2 sm:flex-col sm:w-32 shrink-0">
                        <button
                            type="button"
                            onClick={() => record("accept")}
                            data-testid="cookie-banner-accept"
                            className="flex-1 sm:flex-none bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 min-w-[6rem]"
                        >
                            Accept
                        </button>
                        <button
                            type="button"
                            onClick={() => record("decline")}
                            data-testid="cookie-banner-decline"
                            className="flex-1 sm:flex-none border border-border px-4 py-2 text-sm hover:border-muted-foreground min-w-[6rem]"
                        >
                            Decline
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
